/**
 * DH Discount Confirm Banner
 *
 * Sitewide signal that an auto-applied discount code (e.g. HIPPIE20) is
 * honored. Reads the discount cookie on connect, optimistically shows
 * with a known rate, then verifies against /cart.js and either confirms,
 * adjusts, or hides if the cart rejects the code.
 *
 * Trigger: discount_code cookie present AND not session-dismissed.
 * Hides: cart has items + cart confirms code is not applicable
 *        (e.g. returning customer ineligible for first-order discount).
 */

if (!customElements.get('dh-discount-confirm')) {
  class DHDiscountConfirm extends HTMLElement {
    // Known codes for optimistic first-paint render. Once /cart.js confirms,
    // the rate from cart_level_discount_applications wins.
    static KNOWN_CODES = {
      HIPPIE20: { rate: 20 },
    };
    static DISMISS_KEY = 'dh-discount-confirm-dismissed';

    async connectedCallback() {
      if (this.isDismissed()) return;

      const code = this.readDiscountCookie();
      if (!code) return;

      // Bind dismiss handler regardless
      const dismissBtn = this.querySelector('.dh-discount-confirm__dismiss');
      if (dismissBtn) dismissBtn.addEventListener('click', () => this.dismiss());

      // Optimistic render: show with known code rate immediately
      const known = DHDiscountConfirm.KNOWN_CODES[code];
      if (known) {
        this.renderContent(code, known.rate);
        this.show();
      }

      // Verify against cart in the background
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        const applied = (cart.cart_level_discount_applications || []).find((d) => d.title === code);
        const codeEntry = (cart.discount_codes || []).find((c) => c.code === code);

        if (applied && applied.value_type === 'percentage') {
          // Cart confirms code applied with this rate
          this.renderContent(code, parseFloat(applied.value));
          this.show();
        } else if (cart.items && cart.items.length > 0 && codeEntry && codeEntry.applicable === false) {
          // Cart has items but code is rejected (e.g. ineligible customer).
          // Trust the cart, hide the banner — don't claim what isn't true.
          this.hide();
        }
        // else: cart is empty (can't verify) — keep optimistic display
      } catch (e) {
        // Network failure — keep optimistic display, don't break the page
        console.warn('[dh-discount-confirm] cart fetch failed:', e);
      }
    }

    readDiscountCookie() {
      const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    }

    isDismissed() {
      try {
        return sessionStorage.getItem(DHDiscountConfirm.DISMISS_KEY) === '1';
      } catch (e) {
        return false;
      }
    }

    dismiss() {
      try {
        sessionStorage.setItem(DHDiscountConfirm.DISMISS_KEY, '1');
      } catch (e) { /* sessionStorage unavailable */ }
      this.hide();
    }

    renderContent(code, rate) {
      const codeEl = this.querySelector('.dh-discount-confirm__code');
      const rateEl = this.querySelector('.dh-discount-confirm__rate');
      if (codeEl) codeEl.textContent = code;
      if (rateEl) rateEl.textContent = rate + '% off';
    }

    show() { this.hidden = false; }
    hide() { this.hidden = true; }
  }
  customElements.define('dh-discount-confirm', DHDiscountConfirm);
}
